import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const ConfirmContext = createContext(null);

/**
 * Confirmação em modal nativo (<dialog>), que já traz foco preso e ESC.
 * Em navegadores sem suporte, cai para window.confirm.
 */
export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState(null);
  const dialogRef = useRef(null);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOptions(null);

    if (resolve) {
      resolve(value);
    }
  }, []);

  const confirm = useCallback((config) => {
    const settings = typeof config === 'string' ? { message: config } : config || {};

    const supportsDialog =
      typeof HTMLDialogElement !== 'undefined' &&
      typeof HTMLDialogElement.prototype.showModal === 'function';

    if (!supportsDialog) {
      // eslint-disable-next-line no-alert
      return Promise.resolve(window.confirm(`${settings.title || ''}\n${settings.message || ''}`.trim()));
    }

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOptions(settings);
    });
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog) {
      return;
    }

    if (options && !dialog.open) {
      dialog.showModal();
    } else if (!options && dialog.open) {
      dialog.close();
    }
  }, [options]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <dialog
        ref={dialogRef}
        className="dialog"
        aria-labelledby="confirm-dialog-title"
        onCancel={(event) => {
          event.preventDefault();
          settle(false);
        }}
        onClose={() => settle(false)}
      >
        <div className="dialog__body">
          <h2 id="confirm-dialog-title" className="section-subtitle">
            {options?.title || 'Confirmar ação'}
          </h2>
          {options?.message && <p>{options.message}</p>}
        </div>
        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={() => settle(false)}>
            {options?.cancelLabel || 'Cancelar'}
          </button>
          <button
            type="button"
            className={`btn ${options?.tone === 'danger' ? 'btn--danger' : 'btn--primary'}`}
            onClick={() => settle(true)}
          >
            {options?.confirmLabel || 'Confirmar'}
          </button>
        </div>
      </dialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);

  if (!context) {
    throw new Error('useConfirm precisa estar dentro de ConfirmProvider');
  }

  return context;
};
